import React, { useState, useEffect } from 'react';
import { Typography, Form, Input, Button, Radio, message, Alert, Spin, Card as AntCard, Row, Col, Divider, Modal, DatePicker } from 'antd';
import { Leaf, ArrowLeft, Save, Info, CheckCircle, MessageCircle, Sliders, Bell, BellOff, FileSpreadsheet } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SimpleDashboardLayout from '../components/layout/SimpleDashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { deviceService, IDevice } from '../services/deviceService';
import { calibrationService, ICalibration } from '../services/calibrationService';
import { triggerWeatherBroadcast } from '../services/weatherBroadcastService';
import { userPreferencesService } from '../services/userPreferencesService';
import { exportService } from '../services/exportService';

// Type workaround for Card component
const Card = AntCard as any;

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DhartiNetraForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [existingDevice, setExistingDevice] = useState<IDevice | null>(null);

  // Broadcast trigger state
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Calibration state
  const [calibration, setCalibration] = useState<ICalibration | null>(null);
  const [calibrationLoading, setCalibrationLoading] = useState(false);
  const [showCalibrationForm, setShowCalibrationForm] = useState(false);
  const [calibrationForm] = Form.useForm();

  // Alert preferences state
  const [alertsEnabled, setAlertsEnabled] = useState<boolean>(true);
  const [alertToggleLoading, setAlertToggleLoading] = useState(false);

  // Export modal state
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportType, setExportType] = useState<'all' | 'custom'>('all');
  const [exportDateRange, setExportDateRange] = useState<[string, string] | null>(null);
  const [exportInfo, setExportInfo] = useState<any>(null);

  const { t, language } = useLanguage();

  // Fetch export info on mount
  useEffect(() => {
    fetchExportInfo();
  }, []);

  useEffect(() => {
    fetchDevice();
    fetchAlertPreferences();
  }, []);

  useEffect(() => {
    if (existingDevice?.deviceId) {
      fetchCalibration();
    }
  }, [existingDevice]);

  const fetchDevice = async () => {
    try {
      const devices = await deviceService.list();
      const dnDevice = devices.find(d => d.type === 'dhartinetra');
      if (dnDevice) {
        setExistingDevice(dnDevice);
        form.setFieldsValue({
          deviceId: dnDevice.deviceId,
          name: dnDevice.name || '',
          cropType: dnDevice.cropType,
          frequency: dnDevice.frequency,
          language: dnDevice.preferredLanguage,
        });
      }
    } catch (err) {
      console.error('Failed to fetch devices', err);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchCalibration = async () => {
    if (!existingDevice?.deviceId) return;
    try {
      const cal = await calibrationService.getActiveCalibration(existingDevice.deviceId);
      setCalibration(cal);
    } catch (err) {
      console.error('Failed to fetch calibration', err);
    }
  };

  const fetchAlertPreferences = async () => {
    try {
      const response = await userPreferencesService.getAlertPreferences();
      if (response.success) {
        setAlertsEnabled(response.data.alertsEnabled);
      }
    } catch (err) {
      console.error('Failed to fetch alert preferences', err);
    }
  };

  const handleToggleAlerts = async () => {
    setAlertToggleLoading(true);
    try {
      const newStatus = !alertsEnabled;
      const response = await userPreferencesService.toggleAlertPreferences(newStatus);
      if (response.success) {
        setAlertsEnabled(newStatus);
        message.success(newStatus ? 'WhatsApp alerts enabled' : 'WhatsApp alerts disabled');
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to toggle alerts');
    } finally {
      setAlertToggleLoading(false);
    }
  };

  const fetchExportInfo = async () => {
    try {
      const response = await exportService.getExportInfo();
      if (response.success) {
        setExportInfo(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch export info', err);
    }
  };

  const handleExport = async () => {
    if (exportType === 'custom' && !exportDateRange) {
      message.error('Please select a date range');
      return;
    }

    setExportLoading(true);
    try {
      await exportService.exportSensorData({
        type: exportType,
        startDate: exportDateRange?.[0],
        endDate: exportDateRange?.[1]
      });
      message.success('Export downloaded successfully!');
      setExportModalVisible(false);
      setExportDateRange(null);
      setExportType('all');
    } catch (err: any) {
      message.error(err?.message || 'Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleCalibrationSubmit = async (values: any) => {
    if (!existingDevice?.deviceId) return;
    setCalibrationLoading(true);
    try {
      // Convert local datetime strings to UTC ISO strings for the API
      const localToUTC = (localDateStr: string) => {
        // localDateStr is in format "YYYY-MM-DDTHH:mm" (local time)
        // Create date assuming it's in local timezone, then convert to UTC ISO string
        const localDate = new Date(localDateStr);
        return localDate.toISOString();
      };

      const payload = {
        ...values,
        soilSampleDateTime: localToUTC(values.soilSampleDateTime),
        npkLabTestTime: localToUTC(values.npkLabTestTime),
      };

      await calibrationService.createCalibration(
        existingDevice.deviceId,
        existingDevice._id,
        payload
      );
      message.success('Calibration updated successfully');
      setShowCalibrationForm(false);
      await fetchCalibration();
    } catch (err: any) {
      message.error(err?.message || 'Failed to update calibration');
    } finally {
      setCalibrationLoading(false);
    }
  };

  const handleWeatherBroadcast = async () => {
    setBroadcastLoading(true);
    setBroadcastStatus(null);
    try {
      const result = await triggerWeatherBroadcast();
      setBroadcastStatus({ type: 'success', text: result.message });
      message.success('WhatsApp update queued successfully!');
    } catch (err: any) {
      setBroadcastStatus({ type: 'error', text: err?.message || 'Failed to trigger update' });
      message.error('Failed to queue WhatsApp update');
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setSuccess(false);
    try {
      if (existingDevice) {
        // Edit mode
        const updated = await deviceService.update(existingDevice._id, {
          name: values.name,
          cropType: values.cropType,
          frequency: values.frequency,
          preferredLanguage: values.language,
        });
        setExistingDevice(updated);
        message.success('DhartiNetra device settings updated!');
      } else {
        // Create mode
        const created = await deviceService.create({
          type: 'dhartinetra',
          deviceId: values.deviceId,
          name: values.name,
          cropType: values.cropType,
          frequency: values.frequency,
          preferredLanguage: values.language,
        });
        setExistingDevice(created);
        message.success('DhartiNetra device connected successfully!');
      }
      setSuccess(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save device settings. Please try again.';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <SimpleDashboardLayout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </SimpleDashboardLayout>
    );
  }

  const isEditMode = !!existingDevice;

  return (
    <SimpleDashboardLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Link to="/dashboard" className="mr-4">
              <Button
                type="text"
                icon={<ArrowLeft className="w-4 h-4" />}
                aria-label={t('back')}
              />
            </Link>
            <div>
              <Title level={4} className="mb-0 flex items-center">
                <Leaf className="w-6 h-6 mr-2 text-green-500" />
                {isEditMode ? 'DhartiNetra Settings' : t('dhartinetra_device')}
              </Title>
              <Text type="secondary">
                {isEditMode ? 'Manage your connected device' : t('connect_soil_device')}
              </Text>
            </div>
          </div>
          
          {isEditMode && (
            <Button
              type="default"
              icon={<FileSpreadsheet className="w-4 h-4" />}
              onClick={() => setExportModalVisible(true)}
              size="large"
            >
              Export Data
            </Button>
          )}
        </div>

        {success && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircle className="w-4 h-4" />}
            message={isEditMode ? "Settings Updated!" : "Device connected!"}
            description={
              isEditMode
                ? "Your DhartiNetra device preferences have been saved."
                : "Your DhartiNetra device has been registered. You will receive soil health updates on WhatsApp."
            }
            className="mb-4"
            closable
            onClose={() => setSuccess(false)}
          />
        )}

        <div
          className="p-4 rounded-lg shadow-sm border mb-6"
          style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Form.Item
              name="deviceId"
              label={t('device_id')}
              rules={[
                { required: true, message: t('device_id') },
                {
                  pattern: /^(DN-[A-Z0-9]{6}|DhartiNatra_[A-Z0-9]{12})$/,
                  message: t('device_id_format'),
                },
              ]}
              extra={isEditMode ? "Device ID cannot be changed." : t('device_id_help')}
            >
              <Input
                placeholder="e.g. DN-A12B3C or DhartiNatra_781C3C0B5E0C"
                size="large"
                disabled={isEditMode}
                prefix={<Info className="w-4 h-4 text-gray-400 mr-1" />}
              />
            </Form.Item>

            <Form.Item
              name="name"
              label="Device Name (Optional)"
              extra="Give your device a custom name like 'Farm 1 Sensor'"
            >
              <Input
                placeholder="e.g. Tomato Field Sensor"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="frequency"
              label={t('update_frequency')}
              initialValue="daily"
              rules={[{ required: true, message: t('update_frequency') }]}
            >
              <Radio.Group size="large">
                <Radio.Button value="hourly">{t('hourly') || 'Hourly'}</Radio.Button>
                <Radio.Button value="daily">{t('daily')}</Radio.Button>
                <Radio.Button value="weekly">{t('weekly')}</Radio.Button>
                <Radio.Button value="alerts">{t('alerts_only')}</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {/* WhatsApp Alerts Toggle */}
            <Form.Item label="WhatsApp Alerts">
              <div className="flex items-center gap-3">
                <Button
                  type={alertsEnabled ? 'primary' : 'default'}
                  icon={alertsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                  loading={alertToggleLoading}
                  onClick={handleToggleAlerts}
                  size="large"
                  style={alertsEnabled ? { backgroundColor: '#25D366', borderColor: '#25D366' } : {}}
                >
                  {alertsEnabled ? 'Alerts ON' : 'Alerts OFF'}
                </Button>
                <Text type="secondary">
                  {alertsEnabled 
                    ? 'You will receive WhatsApp updates' 
                    : 'WhatsApp updates are paused. Use webhook STOP/START commands or toggle here.'}
                </Text>
              </div>
            </Form.Item>

            <Form.Item
              name="language"
              label={t('preferred_language')}
              initialValue={language}
              rules={[{ required: true, message: t('preferred_language') }]}
            >
              <Radio.Group size="large">
                <Radio.Button value="marathi">मराठी</Radio.Button>
                <Radio.Button value="hindi">हिंदी</Radio.Button>
                <Radio.Button value="english">English</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="cropType"
              label={t('primary_crop')}
              rules={[{ required: true, message: t('primary_crop') }]}
            >
              <Input
                placeholder={t('crop_placeholder')}
                size="large"
              />
            </Form.Item>

            <Form.Item className="mt-8 mb-0">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<Save className="w-4 h-4" />}
                size="large"
                block
              >
                {isEditMode ? 'Save Setup' : t('connect_device')}
              </Button>
            </Form.Item>
          </Form>
        </div>

        {isEditMode && (
          <>
            {/* Calibration Section */}
            <div
              className="p-4 rounded-lg shadow-sm border mb-6"
              style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Sliders className="w-5 h-5 text-blue-500" />
                <Title level={5} className="mb-0" style={{ color: 'var(--color-text)' }}>
                  Sensor Calibration
                </Title>
              </div>
              
              {calibration ? (
                <Card className="mb-4" size="small">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Text type="secondary">Nitrogen Ratio:</Text>
                      <br />
                      <Text strong>{calibration.soilNitrogenRatio}</Text>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Phosphorus Ratio:</Text>
                      <br />
                      <Text strong>{calibration.soilPhosphorusRatio}</Text>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Potassium Ratio:</Text>
                      <br />
                      <Text strong>{calibration.soilPotassiumRatio}</Text>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">EC Ratio:</Text>
                      <br />
                      <Text strong>{calibration.soilEcRatio}</Text>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">pH Offset:</Text>
                      <br />
                      <Text strong>{calibration.soilPhOffset}</Text>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Last Updated:</Text>
                      <br />
                      <Text strong>{new Date(calibration.recivedDate).toLocaleDateString()}</Text>
                    </Col>
                  </Row>
                </Card>
              ) : (
                <Alert
                  message="No calibration data"
                  description="This device is using default calibration values."
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )}

              <Button
                type="default"
                icon={<Sliders className="w-4 h-4" />}
                onClick={() => setShowCalibrationForm(!showCalibrationForm)}
                className="w-full"
              >
                {showCalibrationForm ? 'Hide Calibration Form' : 'Update Calibration'}
              </Button>

              {showCalibrationForm && (
                <div className="mt-4 p-4 border rounded-lg" style={{ background: 'var(--color-background)' }}>
                  <Form
                    form={calibrationForm}
                    layout="vertical"
                    onFinish={handleCalibrationSubmit}
                    initialValues={{
                      soilSampleDateTime: existingDevice?.timezone 
                        ? new Date().toLocaleString('sv-SE', { timeZone: existingDevice.timezone }).replace(' ', 'T').slice(0, 16)
                        : new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
                      npkLabTestTime: existingDevice?.timezone
                        ? new Date().toLocaleString('sv-SE', { timeZone: existingDevice.timezone }).replace(' ', 'T').slice(0, 16)
                        : new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
                      sampleSoilMoisture: 25,
                      trueSoilNitrogen: 50,
                      trueSoilPhosphorus: 30,
                      trueSoilPotassium: 150,
                      trueSoilEC: 1.5,
                      trueSoilPH: 6.5,
                    }}
                  >
                    <Alert
                      message="Auto-Calibration"
                      description="Ratio values will be automatically calculated by matching the sample datetime with the closest sensor reading and computing: Ratio = True Value / Sensor Value"
                      type="info"
                      showIcon
                      className="mb-4"
                    />
                    
                    <Row gutter={[16, 0]}>
                      <Col span={12}>
                        <Form.Item
                          name="soilSampleDateTime"
                          label="Soil Sample DateTime (Local Time)"
                          rules={[{ required: true, message: 'Sample datetime is required' }]}
                        >
                          <Input type="datetime-local" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="npkLabTestTime"
                          label="Lab Test DateTime (Local Time)"
                          rules={[{ required: true, message: 'Lab test datetime is required' }]}
                        >
                          <Input type="datetime-local" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="sampleSoilMoisture"
                          label="Sample Soil Moisture (%)"
                          rules={[{ required: true, message: 'Soil moisture is required' }]}
                        >
                          <Input type="number" min="0" max="100" step="0.1" />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Divider orientation="left">True Lab Values</Divider>
                    
                    <Row gutter={[16, 0]}>
                      <Col span={12}>
                        <Form.Item
                          name="trueSoilNitrogen"
                          label="True Nitrogen (mg/kg)"
                          rules={[{ required: true, message: 'True nitrogen is required' }]}
                        >
                          <Input type="number" step="0.1" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="trueSoilPhosphorus"
                          label="True Phosphorus (mg/kg)"
                          rules={[{ required: true, message: 'True phosphorus is required' }]}
                        >
                          <Input type="number" step="0.1" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="trueSoilPotassium"
                          label="True Potassium (mg/kg)"
                          rules={[{ required: true, message: 'True potassium is required' }]}
                        >
                          <Input type="number" step="0.1" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="trueSoilEC"
                          label="True EC (dS/m)"
                          rules={[{ required: true, message: 'True EC is required' }]}
                        >
                          <Input type="number" step="0.01" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="trueSoilPH"
                          label="True pH"
                          rules={[{ required: true, message: 'True pH is required' }]}
                        >
                          <Input type="number" min="0" max="14" step="0.1" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item className="mb-0">
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={calibrationLoading}
                        block
                      >
                        Save New Calibration
                      </Button>
                    </Form.Item>
                  </Form>
                </div>
              )}
            </div>

            {/* Manual Sensor Update Section */}
            <div
              className="p-4 rounded-lg shadow-sm border mb-6"
              style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
            >
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-5 h-5 text-green-500" />
              <Title level={5} className="mb-0" style={{ color: 'var(--color-text)' }}>
                Manual Sensor Update
              </Title>
            </div>
            <Text type="secondary" className="block mb-4">
              Get your latest sensor readings sent directly to your WhatsApp number right now.
            </Text>

            {broadcastStatus && (
              <Alert
                type={broadcastStatus.type}
                message={broadcastStatus.text}
                className="mb-3"
                showIcon
                closable
                onClose={() => setBroadcastStatus(null)}
              />
            )}

            <Button
              type="primary"
              icon={<MessageCircle className="w-4 h-4" />}
              loading={broadcastLoading}
              onClick={handleWeatherBroadcast}
              className="w-full"
              style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
            >
              {broadcastLoading ? 'Sending...' : 'Request Update on WhatsApp'}
            </Button>
          </div>
        </>)}

        <div
          className="p-4 rounded-lg border border-green-300 dark:border-green-800"
          style={{ background: 'var(--color-primary-subtle)' }}
        >
          <Text style={{ color: 'var(--color-text)' }}>
            <strong>{t('note')}:</strong> {t('soil_note')}
          </Text>
        </div>

        {/* Export Modal */}
        <Modal
          title="Export Sensor Data"
          open={exportModalVisible}
          onCancel={() => {
            setExportModalVisible(false);
            setExportDateRange(null);
            setExportType('all');
          }}
          footer={[
            <Button 
              key="cancel" 
              onClick={() => {
                setExportModalVisible(false);
                setExportDateRange(null);
                setExportType('all');
              }}
            >
              Cancel
            </Button>,
            <Button
              key="export"
              type="primary"
              loading={exportLoading}
              onClick={handleExport}
              icon={<FileSpreadsheet className="w-4 h-4" />}
            >
              Export Excel
            </Button>
          ]}
        >
          <div className="space-y-4">
            <Radio.Group
              value={exportType}
              onChange={(e: any) => {
                setExportType(e.target.value);
                if (e.target.value === 'all') {
                  setExportDateRange(null);
                }
              }}
              className="flex flex-col gap-3"
            >
              <Radio value="all">
                <div>
                  <div className="font-medium">All Data</div>
                  <div className="text-sm text-gray-500">
                    Export all sensor data from {exportInfo?.maxOldDateDisplay || '90 days ago'} onwards
                  </div>
                  {exportInfo?.totalRecords > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      {exportInfo.totalRecords.toLocaleString()} records available
                    </div>
                  )}
                </div>
              </Radio>
              <Radio value="custom">
                <div className="font-medium">Custom Date Range</div>
              </Radio>
            </Radio.Group>

            {exportType === 'custom' && (
              <div className="ml-6 mt-2">
                <RangePicker
                  style={{ width: '100%' }}
                  onChange={(dates: any) => {
                    if (dates && dates[0] && dates[1]) {
                      setExportDateRange([
                        dates[0].format('YYYY-MM-DD'),
                        dates[1].format('YYYY-MM-DD')
                      ]);
                    } else {
                      setExportDateRange(null);
                    }
                  }}
                  disabledDate={(current: any) => {
                    // Disable dates older than maxOldDays
                    const maxOldDate = exportInfo?.maxOldDate 
                      ? new Date(exportInfo.maxOldDate)
                      : new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
                    return current && current < maxOldDate;
                  }}
                />
                {exportInfo?.maxOldDateDisplay && (
                  <div className="text-xs text-gray-500 mt-2">
                    Data available from: {exportInfo.maxOldDateDisplay}
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      </motion.div>
    </SimpleDashboardLayout>
  );
};

export default DhartiNetraForm;
